import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import { authenticationService } from "../../../services/auth.service";
import { handleFocus } from "../../../utils/masks";

interface EcclesiasticalFunctionForm {
  description: string;
  isMinister: boolean;
  isAuthority: boolean;
}

type EcclesiasticalFunctionParams = {
  id: string;
};

export default function EcclesiasticalFunctionAddEdit() {
  const params = useParams<EcclesiasticalFunctionParams>();
  const navigate = useNavigate();
  const [isDisabled, setDisabled] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isAdding, setAdding] = useState<boolean>(false);

  const schema = yup.object().shape({
    description: yup
      .string()
      .required("Descrição obrigatória")
      .max(100, (val) => `Máximo ${val.max} caracteres`)
      .min(2, (val) => `Mínimo ${val.min} caracteres`),
    isMinister: yup.boolean(),
    isAuthority: yup.boolean()
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState
  } = useForm<EcclesiasticalFunctionForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      description: "",
      isMinister: false,
      isAuthority: false
    }
  });

  useEffect(() => {
    const init = async () => {
      const id = +(params.id ?? 0);
      const adding = id === 0;
      setAdding(adding);

      setDisabled(authenticationService.isReadOnly() || !authenticationService.canChange());

      if (adding) {
        reset({
          description: "",
          isMinister: false,
          isAuthority: false
        });
      }

      if (!adding) {
        await handleEdit(id);
      }
    };

    init();
  }, [params.id]);

  const handleEdit = async (id: number) => {
    setLoading(true);
    try {
      const { data } = await api.get(`ecclesiastical-functions/${id}`);
      const item = data?.item?.item ?? data?.item ?? data;

      if (!item) {
        toast.warning("Registro não encontrado", toastOptions);
        navigate("/sistema/funcoes-eclesiasticas");
        return;
      }

      reset({
        description: item.description ?? "",
        isMinister: Boolean(item.isMinister),
        isAuthority: Boolean(item.isAuthority)
      });
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
      navigate("/sistema/funcoes-eclesiasticas");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: EcclesiasticalFunctionForm) => {
    try {
      const id = +(params.id ?? 0);

      if (!id) {
        await api.post("ecclesiastical-functions", data);
        toast.success("Função eclesiástica cadastrada com sucesso!", toastOptions);

        navigate("/sistema/funcoes-eclesiasticas");
        return;
      }

      await api.put(`ecclesiastical-functions/${id}`, data);
      toast.success("Função eclesiástica atualizada com sucesso!", toastOptions);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  return (
    <div id="page-add-edit-ecclesiastical-function" className="uk-section uk-section-default uk-padding-small">
      {isLoading && (
        <div className="uk-flex uk-flex-center uk-margin-bottom">
          <div uk-spinner="true; ratio:1"></div>
        </div>
      )}
      {!isLoading &&
        <form onSubmit={handleSubmit<EcclesiasticalFunctionForm>(onSubmit)}>
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-1@s">
                <label className="uk-form-label" htmlFor="description">
                  Descrição
                </label>
                <input
                  id="description"
                  name="description"
                  className="uk-input uk-form-small"
                  autoComplete={"off"}
                  onFocus={handleFocus}
                  ref={register}
                  disabled={isDisabled || isLoading}
                />
                <small className="uk-text-danger">{errors.description?.message}</small>
              </div>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label uk-margin-small-right" htmlFor="type">Ministro?</label>
                <input
                  id="isMinister"
                  name="isMinister"
                  className="uk-checkbox"
                  type="checkbox"
                  ref={register}
                  disabled={isDisabled || isLoading}
                />
              </div>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label uk-margin-small-right" htmlFor="type">Autoridade?</label>
                <input
                  id="isAuthority"
                  name="isAuthority"
                  className="uk-checkbox"
                  type="checkbox"
                  ref={register}
                  disabled={isDisabled || isLoading}
                />
              </div>
            </div>
          </div>
          <div className="uk-modal-footer uk-margin-bottom">
            <div className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center" uk-grid="true">
              <button
                className="uk-button uk-button-default uk-button-small uk-margin-left"
                type="button"
                onClick={() => navigate("/sistema/funcoes-eclesiasticas")}
              >
                {isAdding ? "Cancelar" : "Voltar"}
              </button>
              <button
                disabled={formState.isSubmitting || isDisabled}
                className="uk-button uk-button-primary uk-button-small uk-margin-left"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </form>
      }
    </div>
  );
}
